// 类型化 fetch 封装：
// - 自动 credentials: 'include'，让 daemon 的 session cookie 生效。
// - 401 抛 SessionExpiredError，由 useSessionGuard 监听后跳 /login。
// - 其它非 2xx 解析 envelope 抛 APIError，UI 用 ErrorPanel 展示。

import type { APIErrorCode, APIErrorEnvelope } from "./types";

export class SessionExpiredError extends Error {
  readonly path: string;
  constructor(path: string) {
    super("会话已过期，请重新登录");
    this.name = "SessionExpiredError";
    this.path = path;
  }
}

export class APIError extends Error {
  readonly code: APIErrorCode | "unknown";
  readonly status: number;
  readonly path: string;
  constructor(args: {
    code: APIErrorCode | "unknown";
    message: string;
    status: number;
    path: string;
  }) {
    super(args.message);
    this.name = "APIError";
    this.code = args.code;
    this.status = args.status;
    this.path = args.path;
  }
}

async function parseEnvelope(res: Response, path: string): Promise<never> {
  let code: APIErrorCode | "unknown" = "unknown";
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const body = (await res.json()) as APIErrorEnvelope;
      if (body && body.ok === false && body.error) {
        code = body.error.code;
        message = body.error.message;
      }
    }
  } catch {
    /* ignore parse error；使用 statusText */
  }
  throw new APIError({ code, message, status: res.status, path });
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.headers || {}),
    },
  });

  if (res.status === 401) {
    throw new SessionExpiredError(path);
  }
  if (!res.ok) {
    await parseEnvelope(res, path);
  }

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }
  // 非 JSON 响应（极少；保险起见返回 text 包成 unknown）
  return (await res.text()) as unknown as T;
}

export function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, { method: "GET", ...init });
}

export function apiPost<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: body === undefined ? undefined : JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });
}

// apiPostForm：daemon 写接口大多走 net/http 的 request.ParseForm() + FormValue()，
// 因此 SPA 写请求必须用 application/x-www-form-urlencoded body 而不是 JSON。
// 仅当显式 Accept: application/json 时 daemon 才把响应改成 JSON envelope；
// 这里默认带 Accept JSON（apiFetch 会自动加），不依赖额外 query 参数。
export function apiPostForm<T>(
  path: string,
  fields: Record<string, string | number | boolean | undefined | null>,
  init?: RequestInit,
): Promise<T> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    params.set(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
  }
  return apiFetch<T>(path, {
    ...init,
    method: "POST",
    body: params.toString(),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...(init?.headers || {}),
    },
  });
}

export function apiPostURLSearchParams<T>(
  path: string,
  params: URLSearchParams,
  init?: RequestInit,
): Promise<T> {
  return apiFetch<T>(path, {
    ...init,
    method: "POST",
    body: params.toString(),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...(init?.headers || {}),
    },
  });
}
